import { useState, useEffect } from 'react';

interface UseActiveSectionOptions {
  sectionIds: string[];
  rootMargin?: string;
  threshold?: number;
}

export function useActiveSection(options: UseActiveSectionOptions) {
  const { sectionIds, rootMargin = '-20% 0px -80% 0px', threshold = 0.1 } = options;
  const [activeSection, setActiveSection] = useState<string>(sectionIds[0] || '');

  useEffect(() => {
    const observerOptions = {
      rootMargin,
      threshold
    };

    const observer = new IntersectionObserver((entries) => {
      // Find the entry with the largest intersection ratio
      let maxRatio = 0;
      let activeSectionId = activeSection;

      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
          maxRatio = entry.intersectionRatio;
          activeSectionId = entry.target.id;
        }
      });

      // If no section is intersecting, find the closest one based on scroll position
      if (maxRatio === 0) {
        const scrollY = window.scrollY;
        let closestSection = sectionIds[0];
        let minDistance = Infinity;

        sectionIds.forEach((sectionId) => {
          const element = document.getElementById(sectionId);
          if (element) {
            const rect = element.getBoundingClientRect();
            const elementTop = rect.top + scrollY;
            const distance = Math.abs(elementTop - scrollY);
            
            if (distance < minDistance) {
              minDistance = distance;
              closestSection = sectionId;
            }
          }
        });

        activeSectionId = closestSection;
      }

      if (activeSectionId !== activeSection) {
        setActiveSection(activeSectionId);
      }
    }, observerOptions);

    // Observe all sections
    sectionIds.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [sectionIds, rootMargin, threshold, activeSection]);

  return activeSection;
}